import { Select } from "@package/subframe/components/Select";
import { TextArea } from "@package/subframe/components/TextArea";
import { TextField } from "@package/subframe/components/TextField";

import {
  type ExternalServiceField,
  parseExternalServiceFields,
  validateExternalServiceAnswer,
} from "@/components/compliance/external-service-fields";
import type { ComplianceTaskDefinitionResponseDtoExternalServiceConfig } from "@/generated/model";
import { signedDecimalInputProps } from "@/lib/is-ios";
import { sanitizeDecimalInput } from "@/lib/number-input";

interface Props {
  config: ComplianceTaskDefinitionResponseDtoExternalServiceConfig | undefined;
  data: Record<string, unknown>;
  onFieldChange: (key: string, value: unknown) => void;
  notes: string;
  onNotesChange: (notes: string) => void;
}

export function ExternalServiceCompletionForm({
  config,
  data,
  onFieldChange,
  notes,
  onNotesChange,
}: Props) {
  const fields = parseExternalServiceFields(config);

  return (
    <div className="flex w-full flex-col items-start gap-4">
      <span className="text-body font-body text-subtext-color">
        Record the details of the external service that was carried out.
      </span>
      {fields.length === 0 && (
        <span className="text-caption font-caption text-error-700">
          No external service fields are configured. Ask a manager to update the
          task definition.
        </span>
      )}
      {fields.map((field) => {
        const value = data[field.name];
        // Surface type mismatches as the practitioner types. Required-but-empty
        // is communicated by the asterisk and the disabled submit, so we don't
        // nag with a "required" error before they've had a chance to fill it in.
        const error = validateExternalServiceAnswer(field.type, value, false);

        return (
          <div
            key={field.name}
            className="flex w-full flex-col items-start gap-2"
          >
            <label className="text-body-bold font-body-bold text-neutral-900">
              {field.label}
              {field.required && <span className="text-error-700 ml-1">*</span>}
            </label>
            <ExternalServiceFieldControl
              field={field}
              value={value}
              error={!!error}
              onChange={(next) => onFieldChange(field.name, next)}
            />
            {error && (
              <span className="text-caption font-caption text-error-700">
                {error}
              </span>
            )}
          </div>
        );
      })}

      <div className="flex w-full flex-col items-start gap-2 pt-2">
        <label className="text-body-bold font-body-bold text-neutral-900">
          Notes
        </label>
        <span className="text-caption font-caption text-subtext-color">
          Optional — anything else the auditor would need to know.
        </span>
        <TextArea className="h-auto w-full" label="" helpText="">
          <TextArea.Input
            placeholder=""
            value={notes}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
              onNotesChange(e.target.value)
            }
          />
        </TextArea>
      </div>
    </div>
  );
}

interface ControlProps {
  field: ExternalServiceField;
  value: unknown;
  error: boolean;
  onChange: (value: unknown) => void;
}

/** Render the input control matching the field's declared type. */
function ExternalServiceFieldControl({
  field,
  value,
  error,
  onChange,
}: ControlProps) {
  if (field.type === "yes_no") {
    return (
      <Select
        className="h-auto w-full"
        label=""
        placeholder="Select…"
        helpText=""
        error={error}
        value={value as string | undefined}
        onValueChange={onChange}
      >
        <Select.Item value="yes">Yes</Select.Item>
        <Select.Item value="no">No</Select.Item>
      </Select>
    );
  }

  const isNumber = field.type === "number";
  const inputType = field.type === "date" ? "date" : "text";

  return (
    <TextField className="h-auto w-full" label="" helpText="" error={error}>
      <TextField.Input
        type={inputType}
        {...(isNumber ? signedDecimalInputProps() : {})}
        placeholder=""
        value={(value as string | undefined) ?? ""}
        onChange={(e) =>
          onChange(
            isNumber ? sanitizeDecimalInput(e.target.value) : e.target.value,
          )
        }
      />
    </TextField>
  );
}
